import React, { useState } from 'react';
import { Box, Typography, Avatar, Button, Paper, IconButton } from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import EditIcon from '@mui/icons-material/Edit';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import { useSigilContext } from '../../context/sigilContext';
import CommentForm from './CommentForm';

const CommentItem = ({ comment, replies = [] }) => {
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [showReplies, setShowReplies] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const { user } = useUser();
    const { i18n } = useSigilContext();
    const translate = i18n?.forum || {};

    const isAuthor = user && comment.userId === user.id;

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        return new Date(timestamp.seconds * 1000).toLocaleDateString() + ' ' +
            new Date(timestamp.seconds * 1000).toLocaleTimeString();
    };

    const handleLike = async () => {
        if (!user) return;

        const commentRef = doc(db, 'comments', comment.id);
        const likes = comment.likes || [];
        const dislikes = comment.dislikes || [];
        const userLiked = likes.includes(user.id);
        const userDisliked = dislikes.includes(user.id);

        try {
            if (userLiked) {
                // Remove like
                await updateDoc(commentRef, {
                    likes: arrayRemove(user.id)
                });
            } else {
                // Add like and remove dislike if exists
                const updates = {
                    likes: arrayUnion(user.id)
                };
                if (userDisliked) {
                    updates.dislikes = arrayRemove(user.id);
                }
                await updateDoc(commentRef, updates);
            }
        } catch (error) {
            console.error("Error updating like:", error);
        }
    };

    const handleDislike = async () => {
        if (!user) return;

        const commentRef = doc(db, 'comments', comment.id);
        const likes = comment.likes || [];
        const dislikes = comment.dislikes || [];
        const userLiked = likes.includes(user.id);
        const userDisliked = dislikes.includes(user.id);

        try {
            if (userDisliked) {
                // Remove dislike
                await updateDoc(commentRef, {
                    dislikes: arrayRemove(user.id)
                });
            } else {
                // Add dislike and remove like if exists
                const updates = {
                    dislikes: arrayUnion(user.id)
                };
                if (userLiked) {
                    updates.likes = arrayRemove(user.id);
                }
                await updateDoc(commentRef, updates);
            }
        } catch (error) {
            console.error("Error updating dislike:", error);
        }
    };

    const likes = comment.likes || [];
    const dislikes = comment.dislikes || [];
    const userLiked = user && likes.includes(user.id);
    const userDisliked = user && dislikes.includes(user.id);

    return (
        <Box sx={{ marginBottom: 2 }}>
            <Paper elevation={0} sx={{
                padding: 2,
                backgroundColor: 'transparent',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '18px'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', color: 'secondary.main', marginRight: 1 }}>
                        {comment.userName ? comment.userName.charAt(0).toUpperCase() : '?'}
                    </Avatar>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingLeft: '20px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {comment.userName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            {formatDate(comment.createdAt)}
                        </Typography>
                    </Box>
                </Box>

                {isEditing ? (
                    <CommentForm 
                        isEditing={true} 
                        initialText={comment.text} 
                        commentId={comment.id}
                        onCancel={() => setIsEditing(false)}
                        onSuccess={() => setIsEditing(false)}
                    />
                ) : (
                    <Typography variant="body1" sx={{ marginBottom: 2, whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                        {comment.text}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {!isEditing && (
                        <Button
                            startIcon={<ReplyIcon />}
                            size="small"
                            onClick={() => setShowReplyForm(!showReplyForm)}
                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                            {translate.replyButton || 'Responder'}
                        </Button>
                    )}

                    {isAuthor && !isEditing && (
                        <Button
                            startIcon={<EditIcon />}
                            size="small"
                            onClick={() => setIsEditing(true)}
                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                            {translate.editButton || 'Editar'}
                        </Button>
                    )}

                    {replies.length > 0 && (
                        <Button
                            size="small"
                            onClick={() => setShowReplies(!showReplies)}
                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                            {showReplies ? (translate.hideReplies || 'Ocultar respuestas') : (translate.showReplies?.replace('{count}', replies.length) || `Ver ${replies.length} respuestas`)}
                        </Button>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', marginLeft: 'auto' }}>
                        <IconButton
                            size="small"
                            onClick={handleLike}
                            sx={{
                                color: userLiked ? '#4CAF50' : 'rgba(255,255,255,0.7)',
                                '&:hover': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                }
                            }}
                        >
                            <ThumbUpIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', minWidth: '20px' }}>
                            {likes.length}
                        </Typography>

                        <IconButton
                            size="small"
                            onClick={handleDislike}
                            sx={{
                                color: userDisliked ? '#f44336' : 'rgba(255,255,255,0.7)',
                                '&:hover': {
                                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                }
                            }}
                        >
                            <ThumbDownIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', minWidth: '20px' }}>
                            {dislikes.length}
                        </Typography>
                    </Box>
                </Box>

                {showReplyForm && (
                    <Box sx={{ marginLeft: 4, marginTop: 2 }}>
                        <CommentForm
                            parentId={comment.id}
                            onCancel={() => setShowReplyForm(false)}
                            onSuccess={() => {
                                setShowReplyForm(false);
                                setShowReplies(true);
                            }}
                        />
                    </Box>
                )}
            </Paper>

            {/* Render Replies */}
            {showReplies && replies.length > 0 && (
                <Box sx={{ marginLeft: 4, marginTop: 2, borderLeft: '2px solid rgba(255,255,255,0.1)', paddingLeft: 2 }}>
                    {replies.map(reply => (
                        <CommentItem
                            key={reply.id}
                            comment={reply}
                            replies={reply.replies}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default CommentItem;
